import { Columns, ComparePairedAnalysisV2 } from 'api/data/analyses';
import { isAnalysisInputValid } from 'helpers/analysis';
import { VariablesData } from 'store/data/variables';
import { ActionTypes, VariablesDataSelectItems } from 'store/data/analyses';

import { ComparePairedViewV2 } from './ComparePairedViewV2/ComparePairedViewV2';
import { ComparePairedConfigV2 } from './ComparePairedConfigV2/ComparePairedConfigV2';

import { InvalidAnalysis } from '..';
import { AnalysisContainer, AnalysisViewContainer } from '../UI';
import { MediaQueries } from 'environment';
import {
	useFetchAnalysis,
	useAnalysesActiveColum,
	useFullscreenAnalysis,
	useTranslation
} from 'hooks/store';
import { useMediaQuery } from 'hooks/utils';
import { withMemo } from 'helpers/HOCs';
import { Loader } from 'components/UI/Loader';

interface Props {
	analysis: ComparePairedAnalysisV2;
	variablesData: VariablesData;
	variablesDataSelectItems: VariablesDataSelectItems;
	exporting: boolean;
}

function Component({ analysis, variablesData, variablesDataSelectItems, exporting }: Props) {
	const { translate } = useTranslation();
	const analysisValidInput = isAnalysisInputValid(analysis);

	const { loading } = useFetchAnalysis({
		id: analysis.id,
		actionType: ActionTypes.GET_COMPARE_PAIRED_V2,
		refetchDependencies: [...(analysisValidInput ? ['input.variables'] : [])],
		initialFetchDependency: 'output.dataset.data'
	});

	const [activeColumn] = useAnalysesActiveColum();
	const [fullscreen, setFullscreenAnalysis] = useFullscreenAnalysis();

	const isMobileDevice = useMediaQuery(
		`only screen and ${MediaQueries.minWidth.xs} and ${MediaQueries.maxWidth.sm}`
	);

	const handleFullScreen = () => {
		if (
			(activeColumn !== Columns.OneColumn && !isMobileDevice) ||
			(activeColumn === Columns.OneColumn && isMobileDevice)
		) {
			setFullscreenAnalysis({ fullscreen: analysis.id });
		}
	};

	if (analysisValidInput) {
		return (
			<AnalysisContainer onClick={() => handleFullScreen()} activeColumn={activeColumn}>
				{!loading ? (
					<ComparePairedViewV2
						analysis={analysis}
						exporting={exporting}
						variablesData={variablesData}
						loading={loading}
					/>
				) : (
					<AnalysisViewContainer isConfigPanelOpen={analysis.options.configPanel.open}>
						<Loader primary />
					</AnalysisViewContainer>
				)}
				{(analysis.options.configPanel.open &&
					activeColumn === Columns.OneColumn &&
					!isMobileDevice) ||
				(analysis.options.configPanel.open && fullscreen) ? (
					<ComparePairedConfigV2
						analysis={analysis}
						variablesDataSelectItems={variablesDataSelectItems}
						loading={loading}
					/>
				) : null}
			</AnalysisContainer>
		);
	}

	return (
		<AnalysisContainer invalid>
			<InvalidAnalysis
				{...(!analysisValidInput
					? {
							headerMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputHeader
							)
					  }
					: {})}
				{...(!analysisValidInput
					? {
							bodyMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputBody
							)
					  }
					: {})}
				activeColumn={activeColumn}
				fullScreen={!!fullscreen}
				isConfigPanelOpen={analysis.options.configPanel.open}
				category
				numeric
			/>
			{(analysis.options.configPanel.open &&
				activeColumn === Columns.OneColumn &&
				!isMobileDevice) ||
			(analysis.options.configPanel.open && (analysis.activeTab === 1 || fullscreen)) ? (
				<ComparePairedConfigV2
					analysis={analysis}
					variablesDataSelectItems={variablesDataSelectItems}
					loading={loading}
				/>
			) : null}
		</AnalysisContainer>
	);
}

export const ComparePairedV2 = withMemo(Component, [
	'analysis',
	'variablesData',
	'variablesDataSelectItems',
	'exporting'
]);
